import type { PatientActivityExportFormat, PatientActivityExportSummary } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ErrorBanner } from '@/components/ui/state-views';
import { downloadPatientActivityExport, getPatientActivityExportSummary } from '@/config/api-helpers';
import { formatDate } from '@/utils/format';

const SUMMARY_FIELDS: Array<{
  key: keyof Omit<PatientActivityExportSummary, 'patientId' | 'estimatedAt'>;
  label: string;
}> = [
  { key: 'totalActivities', label: 'Activities' },
  { key: 'mealCount', label: 'Meals' },
  { key: 'giSymptomsCount', label: 'GI symptoms' },
  { key: 'movementCount', label: 'Movement' },
  { key: 'hydrationCount', label: 'Hydration' },
  { key: 'mealIngredientMacroCount', label: 'Ingredients' },
  { key: 'mealInsightsCount', label: 'Meal insights' },
  { key: 'reactionCount', label: 'Reactions' },
];

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function fallbackFileName(format: PatientActivityExportFormat) {
  return `nourish_activity_export.${format}`;
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className='rounded-md border border-neutral-200 bg-white px-3 py-2'>
      <div className='text-xs text-neutral-500'>{label}</div>
      <div className='mt-1 text-lg font-semibold text-neutral-900'>{value.toLocaleString()}</div>
    </div>
  );
}

export function ActivityExportTab({ patientId }: { patientId: number }) {
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingFormat, setDownloadingFormat] = useState<PatientActivityExportFormat | null>(null);
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['patient-activity-export-summary', patientId],
    queryFn: () => getPatientActivityExportSummary(patientId),
  });

  const handleDownload = async (format: PatientActivityExportFormat) => {
    setDownloadingFormat(format);
    setDownloadError(null);

    try {
      const { blob, fileName } = await downloadPatientActivityExport(patientId, format);
      triggerDownload(blob, fileName ?? fallbackFileName(format));
    } catch (err) {
      setDownloadError(errorMessage(err));
    } finally {
      setDownloadingFormat(null);
    }
  };

  if (error) {
    return (
      <ErrorBanner
        message={error.message ?? 'Activity export summary could not be loaded'}
        onRetry={() => refetch()}
        title='Activity export is unavailable'
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div className='space-y-4 p-4' aria-label='Loading activity export summary'>
        <div className='h-4 w-48 animate-pulse rounded bg-neutral-200' />
        <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
          {SUMMARY_FIELDS.map((field) => (
            <div key={field.key} className='h-20 animate-pulse rounded-md border border-neutral-200 bg-neutral-100' />
          ))}
        </div>
      </div>
    );
  }

  const hasNoRows = data.totalActivities === 0;

  return (
    <div className='space-y-4 p-4'>
      {downloadError && (
        <ErrorBanner className='mb-2' message={downloadError} retryInSeconds={0} title='Download failed' />
      )}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div className='min-w-0'>
          <h3 className='text-base font-semibold text-neutral-900'>Activity Export</h3>
          <p className='mt-1 text-sm text-neutral-600'>
            Excludes photos and internal patient, activity, and provider IDs.
          </p>
          <p className='mt-1 text-xs text-neutral-500'>Estimated {formatDate(data.estimatedAt)}</p>
        </div>

        <div className='flex shrink-0 flex-col gap-2 sm:flex-row'>
          {(['csv', 'json'] as const).map((format) => (
            <button
              key={format}
              className='inline-flex h-9 items-center justify-center rounded-md bg-neutral-900 px-3 text-sm font-medium uppercase text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50'
              disabled={hasNoRows || downloadingFormat != null}
              onClick={() => handleDownload(format)}
              type='button'
            >
              {downloadingFormat === format ? 'Preparing' : format}
            </button>
          ))}
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
        {SUMMARY_FIELDS.map((field) => (
          <MetricCard key={field.key} label={field.label} value={data[field.key]} />
        ))}
      </div>

      {hasNoRows && (
        <div className='rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600'>
          No activity rows found for this patient.
        </div>
      )}
    </div>
  );
}
